import commonUtil from "../../../util/common-utils";
import { Actions } from "../../../constants/actions";

export interface BrandItem {
    brandId: string;
    brandName: string;
    brandIndex?: string;
    productsCount?: number | null;
}

export interface SearchBrandModalRenderParams {
    popularRoot: HTMLElement;
    allRoot: HTMLElement;
    popular: BrandItem[];
    all: BrandItem[];
    selectedIds: Set<string>;
    filterText: string;
    showAll?: boolean;
}

type BrandCountValue = number | null | undefined;

function toNonNegativeIntOrNull(raw: BrandCountValue): number | null {
    if (typeof raw === "number") {
        return Number.isFinite(raw) ? Math.max(0, Math.trunc(raw)) : null;
    }
    return null;
}

function createBrandGroups(): Map<string, BrandItem[]> {
    return new Map();
}

export class SearchBrandModalRender {
    public static render(params: SearchBrandModalRenderParams): void {
        const { popularRoot, allRoot, popular, all, selectedIds, filterText, showAll } = params;
        const shouldShowAll = showAll !== false;

        const popularFiltered = shouldShowAll
            ? popular
            : popular.filter((brand) => {
                  const count = toNonNegativeIntOrNull(brand.productsCount);
                  return count === null || count > 0;
              });

        const popularHtml = SearchBrandModalRender.renderButtons(popularFiltered, selectedIds);
        popularRoot.innerHTML = popularHtml || '<div class="search-brand-modal__empty" data-translate="empty">-</div>';

        const filtered = SearchBrandModalRender.filterAll(all, filterText, shouldShowAll);
        const allHtml = SearchBrandModalRender.renderGroupedButtons(filtered, selectedIds);
        allRoot.innerHTML = allHtml || '<div class="search-brand-modal__empty" data-translate="empty">-</div>';
    }

    public static getIndexKey(name: string, brandIndex?: string): string {
        const normalizedIndex = String(brandIndex || "")
            .trim()
            .toUpperCase();
        if (normalizedIndex === "0-9") return "0-9";
        if (normalizedIndex === "OTHER") return "Other";
        if (/^[A-Z]$/.test(normalizedIndex)) return normalizedIndex;

        const value = String(name || "").trim();
        if (!value) return "Other";

        const firstCharacter = value.charAt(0);
        if (/[0-9]/.test(firstCharacter)) return "0-9";
        if (/[A-Za-z]/.test(firstCharacter)) return firstCharacter.toUpperCase();
        return "Other";
    }

    public static renderGroupedButtons(list: BrandItem[], selectedIds: Set<string>): string {
        const rows = Array.isArray(list) ? list : [];
        if (rows.length === 0) return "";

        const groups = createBrandGroups();
        rows.forEach((brand) => {
            const key = SearchBrandModalRender.getIndexKey(String(brand.brandName || ""), brand.brandIndex);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)?.push(brand);
        });

        const keys = ["0-9", ...Array.from({ length: 26 }, (_, index) => String.fromCharCode(65 + index)), "Other"];

        return keys
            .filter((key) => (groups.get(key) || []).length > 0)
            .map((key) => {
                const html = SearchBrandModalRender.renderButtons(groups.get(key) || [], selectedIds);
                if (!html) return "";

                return `
                    <div class="search-brand-modal__index-title" data-index="${commonUtil.escapeHtmlAttribute(
                        key,
                    )}">${commonUtil.escapeHtml(key)}</div>
                    ${html}
                `;
            })
            .filter(Boolean)
            .join("");
    }

    public static renderButtons(list: BrandItem[], selectedIds: Set<string>): string {
        const rows = Array.isArray(list) ? list : [];
        if (rows.length === 0) return "";

        return rows
            .map((brand) => {
                const id = String(brand.brandId || "");
                const name = String(brand.brandName || id);
                const isSelected = selectedIds.has(id);
                const selectedAttrs = isSelected ? ' data-selected="1" aria-pressed="true"' : ' aria-pressed="false"';
                const selectedClass = isSelected ? " search-brand-option--selected" : "";

                const count = toNonNegativeIntOrNull(brand.productsCount);
                const hasCount = count !== null;
                const isZero = hasCount && count === 0;
                const zeroClass = isZero ? " search-picker-option--zero" : "";
                const countHtml = hasCount
                    ? `<span class="search-picker-option__count" aria-label="count">${commonUtil.escapeHtml(String(count))}</span>`
                    : "";

                return `<button type="button" class="search-brand-option${selectedClass}${zeroClass}" data-action="${Actions.SELECT_BRAND}" data-brand-id="${commonUtil.escapeHtmlAttribute(
                    id,
                )}"${selectedAttrs}><span class="search-picker-option__name">${commonUtil.escapeHtml(name)}</span>${countHtml}</button>`;
            })
            .join("");
    }

    public static filterAll(list: BrandItem[], filterText: string, shouldShowAll: boolean): BrandItem[] {
        const rows = Array.isArray(list) ? list : [];
        const query = String(filterText || "")
            .trim()
            .toLowerCase();

        const filteredByName = !query
            ? rows
            : rows.filter((brand) =>
                  String(brand.brandName || "")
                      .toLowerCase()
                      .includes(query),
              );

        if (shouldShowAll) return filteredByName;
        return filteredByName.filter((brand) => {
            const count = toNonNegativeIntOrNull(brand.productsCount);
            return count === null || count > 0;
        });
    }
}
